import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import type { FormStepProps } from "../../types/application";
import { resumeParserService } from "../../services/resumeParser";
import { translations } from "../../i18n/translations";
import { useLanguage } from "../../context/LanguageContext";
import { isMobile } from "react-device-detect";
import "../../assets/gcweb-theme.css";
import "../../assets/gcweb-font-fix.css";

const ResumeUploadStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "processing" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [processingStage, setProcessingStage] = useState<string>("");

  const { language } = useLanguage();
  const t = translations[language];
  const showExtractedInfoMessage = data.AIConsentStatus === "Yes";

  const handleFileUpload = useCallback(
    async (file: File) => {
      // Validate file before uploading
      const validation = resumeParserService.validateFile(file, language);
      if (!validation.valid) {
        setUploadStatus("error");
        setErrorMessage(validation.error || t.invalidFile);
        return;
      }

      // Reset states
      setUploadStatus("uploading");
      setErrorMessage("");
      setIsUploading(true);
      setUploadProgress(0);

      try {
        // Simulate progress during upload and processing
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            if (prev < 30) {
              setProcessingStage(t.uploading);
              setUploadStatus("uploading");
            } else if (prev < 70) {
              setProcessingStage(t.processing);
              setUploadStatus("processing");
            } else {
              setProcessingStage(t.extracting);
              setUploadStatus("processing");
            }
            return prev + 10;
          });
        }, 500);

        // Use a simplified parsing approach that works with the backend
        const parsedData = await resumeParserService.uploadAndParseResume(
          file,
          data.AIConsentStatus,
          language
        );

        // Success! Update form data with parsed information
        clearInterval(progressInterval);
        setUploadStatus("success");
        setUploadProgress(100);
        setProcessingStage(t.processed);

        // Update form data with parsed information
        onUpdate({
          ...data, // Keep existing data
          ...parsedData, // Merge with parsed data
          documentId: parsedData.documentId as string,
          resumeFile: file, // Keep the file reference
          AIConsentStatus: data.AIConsentStatus, // Never let parsed data overwrite the user's explicit choice
        });

        setTimeout(() => {
          setIsUploading(false);
        }, 500);
      } catch (error) {
        // Clear any running intervals
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStatus("error");

        let msg = t.processingFailed2;
        if (error instanceof Error) {
          msg = error.message;
        }

        setErrorMessage(msg);
        console.error("Resume processing failed:", error);
      }
    },
    [data, language, onUpdate, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();

      const file = e.dataTransfer.files[0];

      if (file) {
        handleFileUpload(file);
      } else {
        setUploadStatus("error");
        setErrorMessage(t.filesErrorMessage);
      }
    },
    [handleFileUpload, t]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <div>
      {/* Upload Area */}
      <div className="form-group">
        <h3 className="text-lg font-semibold text-gray-900 mrgn-tp-0">
          {t.resumeUploadHeader}
        </h3>
        <p className="text-lg text-gray-700 mrgn-bttm-md">
          {t.supportedfilesformats}
        </p>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
          }}
        >
          {isUploading ? (
            <div className="space-y-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                  uploadStatus === "success"
                    ? "bg-green-100"
                    : uploadStatus === "error"
                      ? "bg-red-100"
                      : "bg-primary-100"
                }`}
              >
                {uploadStatus === "success" ? (
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : uploadStatus === "error" ? (
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className={`w-8 h-8 ${
                      uploadStatus === "processing" ? "text-green-600" : "text-primary-600"
                    } animate-pulse`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                )}
              </motion.div>

              <div>
                <p
                  className={`text-lg font-medium ${
                    uploadStatus === "success"
                      ? "text-green-900"
                      : uploadStatus === "error"
                        ? "text-red-900"
                        : "text-gray-900"
                  }`}
                >
                  {uploadStatus === "success"
                    ? t.processed
                    : uploadStatus === "error"
                      ? t.processingFailed
                      : processingStage || t.resumeProcessing}
                </p>

                {uploadStatus !== "error" && (
                  <>
                    <div className="mt-2 w-64 mx-auto bg-gray-200 rounded-full h-2">
                      <motion.div
                        className={`h-2 rounded-full ${
                          uploadStatus === "success"
                            ? "bg-green-500"
                            : uploadStatus === "processing"
                              ? "bg-green-400"
                              : "bg-primary-500"
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      {uploadProgress}% {t.complete}
                    </p>
                  </>
                )}

                {uploadStatus === "error" && errorMessage && (
                  <div className="alert alert-danger mrgn-tp-md">
                    <p>{errorMessage}</p>
                    <button
                      onClick={() => {
                        setUploadStatus("idle");
                        setErrorMessage("");
                        setIsUploading(false);
                        setUploadProgress(0);
                      }}
                      className="btn btn-danger btn-sm mrgn-tp-sm"
                    >
                      {t.tryagain}
                    </button>
                  </div>
                )}

                {uploadStatus === "success" && showExtractedInfoMessage && (
                  <p className="text-sm text-green-800 mt-2">{t.autoMove}</p>
                )}
              </div>
            </div>
          ) : data.resumeFile ? (
            <div className="space-y-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center"
              >
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </motion.div>

              <div>
                <p className="text-lg font-medium text-gray-900">{t.resumeReceived}</p>
                <p className="text-gray-600">{data.resumeFile.name}</p>
                {showExtractedInfoMessage && (
                  <p className="text-sm text-green-800 mt-2">{t.autoExtract}</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={onNext}
                  className="w-full sm:w-auto px-6 py-3 rounded-lg border-2 border-[#2B4380] font-medium transition-colors bg-[#D7E5F5] text-[#2B4380] hover:bg-[#D0DFF0]"
                >
                  {t.continueReview}
                </button>
              </div>

              {/* Previous navigation */}
              {isMobile ? (
                <div className="flex flex-col sm:flex-row gap-3 pt-3">
                  <button
                    type="button"
                    onClick={onPrevious}
                    className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.previousButton}
                  </button>
                </div>
              ) : (
                <div className="flex justify-between pt-3">
                  <button
                    type="button"
                    onClick={onPrevious}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.previousButton}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="mrgn-tp-md">
                <label htmlFor="resume-file-input" className="wb-inv">
                  {t.choosefile}
                </label>
                <div className="resume-file-control">
                  <span className="resume-file-button-wrapper">
                    <input
                      id="resume-file-input"
                      type="file"
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileSelect}
                      className="resume-file-input-native"
                    />
                    <span className="resume-file-button" aria-hidden="true">
                      {t.choosefile}
                    </span>
                  </span>
                  <span className="resume-file-status" aria-hidden="true">
                    {t.nofilechosen}
                  </span>
                </div>
              </div>

              {/* Previous navigation (kept separate so Skip button remains in place) */}
              {isMobile ? (
                <div className="flex flex-col sm:flex-row gap-3 pt-3">
                  <button
                    type="button"
                    onClick={onPrevious}
                    className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.previousButton}
                  </button>
                </div>
              ) : (
                <div className="flex justify-between pt-3">
                  <button
                    type="button"
                    onClick={onPrevious}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t.previousButton}
                  </button>
                </div>
              )}

              {/* Error display for non-uploading state */}
              {uploadStatus === "error" && !isUploading && errorMessage && (
                <div className="alert alert-danger mrgn-tp-md">
                  <p>{errorMessage}</p>
                  <button
                    onClick={() => {
                      setUploadStatus("idle");
                      setErrorMessage("");
                    }}
                    className="btn btn-danger btn-sm mrgn-tp-sm"
                  >
                    {t.dismiss}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadStep;